import React, { useState } from 'react';
import { CalendarIcon, MailIcon, LinkedinIcon, PhoneIcon, PlusIcon, TrashIcon, ArrowUpDownIcon, CheckCircleIcon, InfoIcon, ClockIcon } from 'lucide-react';
interface OutreachSequenceProps {
  personId: number;
  personName: string;
  initialMessage: string;
  onShowToast?: (message: string, type: 'success' | 'error') => void;
}
type SequenceStep = {
  id: number;
  channel: 'email' | 'linkedin' | 'call';
  days: number;
  message: string;
  subject?: string;
};
export const OutreachSequence: React.FC<OutreachSequenceProps> = ({
  personId,
  personName,
  initialMessage,
  onShowToast
}) => {
  const [sequence, setSequence] = useState<SequenceStep[]>([{
    id: 1,
    channel: 'linkedin',
    days: 0,
    message: initialMessage
  }]);
  const [showForm, setShowForm] = useState(false);
  const [newStep, setNewStep] = useState<Partial<SequenceStep>>({
    channel: 'email',
    days: 3,
    message: '',
    subject: ''
  });
  const [sequenceActive, setSequenceActive] = useState(false);
  // Calculate the total sequence duration in days
  const totalDuration = sequence.reduce((total, step, index) => {
    if (index === 0) return 0;
    return total + step.days;
  }, 0);
  const handleAddStep = () => {
    if (!newStep.message) {
      onShowToast && onShowToast('Please add a message for this step', 'error');
      return;
    }
    const newId = Math.max(0, ...sequence.map(s => s.id)) + 1;
    setSequence([...sequence, {
      id: newId,
      channel: newStep.channel as 'email' | 'linkedin' | 'call',
      days: newStep.days || 0,
      message: newStep.message,
      subject: newStep.subject
    }]);
    setNewStep({
      channel: 'email',
      days: 3,
      message: '',
      subject: ''
    });
    setShowForm(false);
    onShowToast && onShowToast('Step added to sequence', 'success');
  };
  const handleRemoveStep = (id: number) => {
    setSequence(sequence.filter(step => step.id !== id));
    onShowToast && onShowToast('Step removed from sequence', 'success');
  };
  const handleMoveStep = (id: number, direction: 'up' | 'down') => {
    const index = sequence.findIndex(step => step.id === id);
    if (direction === 'up' && index === 0 || direction === 'down' && index === sequence.length - 1) {
      return;
    }
    const newSequence = [...sequence];
    const step = newSequence[index];
    if (direction === 'up') {
      newSequence[index] = newSequence[index - 1];
      newSequence[index - 1] = step;
    } else {
      newSequence[index] = newSequence[index + 1];
      newSequence[index + 1] = step;
    }
    setSequence(newSequence);
  };
  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <MailIcon size={16} className="text-blue-600" data-id="element-718" />;
      case 'linkedin':
        return <LinkedinIcon size={16} className="text-blue-800" data-id="element-719" />;
      case 'call':
        return <PhoneIcon size={16} className="text-green-600" data-id="element-720" />;
      default:
        return null;
    }
  };
  const handleActivateSequence = () => {
    setSequenceActive(true);
    onShowToast && onShowToast(`Sequence activated for ${personName}! The first message will be sent immediately.`, 'success');
  };
  return <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6" data-id="element-721">
      <div className="flex justify-between items-center mb-4" data-id="element-722">
        <div data-id="element-723">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-724">
            Outreach Sequence for {personName}
          </h2>
          <p className="text-sm text-gray-500 mt-1" data-id="element-725">
            {sequenceActive ? 'Active sequence' : 'Create a multi-step outreach sequence'}
            {sequence.length > 1 && ` • ${sequence.length} steps over ${totalDuration} days`}
          </p>
        </div>
        {!sequenceActive && <button onClick={() => setShowForm(!showForm)} className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-white bg-[#f9b30f] hover:bg-[#e0a40e]" data-id="element-726">
            <PlusIcon size={16} className="mr-1" data-id="element-727" />
            {showForm ? 'Cancel' : 'Add Step'}
          </button>}
      </div>
      {sequenceActive && <div className="mb-4 p-3 bg-green-50 border border-green-100 rounded-md flex items-center" data-id="element-728">
          <CheckCircleIcon className="text-green-500 mr-2" size={18} data-id="element-729" />
          <div data-id="element-730">
            <p className="text-sm text-green-700 font-medium" data-id="element-731">
              Sequence is active
            </p>
            <p className="text-xs text-green-600" data-id="element-732">
              First message sent today at 10:30 AM
            </p>
          </div>
        </div>}
      {showForm && !sequenceActive && <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200" data-id="element-733">
          <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-734">
            Add New Sequence Step
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-735">
            <div data-id="element-736">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-737">
                Channel
              </label>
              <select value={newStep.channel} onChange={e => setNewStep({
            ...newStep,
            channel: e.target.value as any
          })} className="block w-full pl-3 pr-10 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" data-id="element-738">
                <option value="linkedin" data-id="element-739">LinkedIn Message</option>
                <option value="email" data-id="element-740">Email</option>
                <option value="call" data-id="element-741">Phone Call</option>
              </select>
            </div>
            <div data-id="element-742">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-743">
                Days After Previous
              </label>
              <input type="number" min="0" value={newStep.days} onChange={e => setNewStep({
            ...newStep,
            days: parseInt(e.target.value)
          })} className="block w-full pl-3 pr-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" data-id="element-744" />
            </div>
          </div>
          {newStep.channel === 'email' && <div className="mb-4" data-id="element-745">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-746">
                Subject
              </label>
              <input type="text" value={newStep.subject || ''} onChange={e => setNewStep({
          ...newStep,
          subject: e.target.value
        })} placeholder="Email subject line" className="block w-full pl-3 pr-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" data-id="element-747" />
            </div>}
          <div className="mb-4" data-id="element-748">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-749">
              Message
            </label>
            <textarea value={newStep.message} onChange={e => setNewStep({
          ...newStep,
          message: e.target.value
        })} rows={4} placeholder="Write your message..." className="block w-full pl-3 pr-3 py-2 text-sm border-gray-300 focus:outline-none focus:ring-[#f9b30f] focus:border-[#f9b30f] rounded-md" data-id="element-750"></textarea>
          </div>
          <div className="flex justify-end space-x-2" data-id="element-751">
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-50" data-id="element-752">
              Cancel
            </button>
            <button onClick={handleAddStep} disabled={!newStep.message} className="px-3 py-1.5 bg-[#f9b30f] hover:bg-[#e0a40e] text-sm font-medium rounded-md text-white disabled:opacity-50" data-id="element-753">
              Add to Sequence
            </button>
          </div>
        </div>}
      <div className="mb-4" data-id="element-754">
        <div className="relative" data-id="element-755">
          {sequence.map((step, index) => <div key={step.id} className="mb-6 relative" data-id="element-756">
              <div className="flex items-center mb-2" data-id="element-757">
                <div className="flex items-center bg-white z-10" data-id="element-758">
                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center mr-2 ${sequenceActive ? index === 0 ? 'bg-green-100' : 'bg-blue-100' : 'bg-blue-100'}`} data-id="element-759">
                    {sequenceActive && index === 0 ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-760" /> : getChannelIcon(step.channel)}
                  </div>
                  <div className="font-medium" data-id="element-761">
                    {index === 0 ? 'Initial Contact' : `Follow-up #${index}`}
                    <span className="ml-2 text-sm font-normal text-gray-500" data-id="element-762">
                      {index > 0 ? `(Day ${step.days})` : '(Today)'}
                    </span>
                  </div>
                </div>
                <div className="ml-auto flex items-center" data-id="element-763">
                  {sequenceActive ? index === 0 ? <span className="text-xs text-green-600 flex items-center" data-id="element-764">
                        <CheckCircleIcon size={12} className="mr-1" data-id="element-765" />
                        Sent
                      </span> : <span className="text-xs text-gray-500 flex items-center" data-id="element-766">
                        <ClockIcon size={12} className="mr-1" data-id="element-767" />
                        Scheduled
                      </span> : <>
                      <button onClick={() => handleMoveStep(step.id, 'up')} disabled={index === 0} className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30" data-id="element-768">
                        <ArrowUpDownIcon size={16} data-id="element-769" />
                      </button>
                      <button onClick={() => handleRemoveStep(step.id)} disabled={index === 0} className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30" data-id="element-770">
                        <TrashIcon size={16} data-id="element-771" />
                      </button>
                    </>}
                </div>
              </div>
              {step.channel === 'email' && step.subject && <div className="ml-10 mb-1 text-sm" data-id="element-772">
                  <span className="font-medium" data-id="element-773">Subject:</span> {step.subject}
                </div>}
              <div className="ml-10 p-3 bg-gray-50 rounded-md border border-gray-200 text-sm text-gray-800 whitespace-pre-wrap" data-id="element-774">
                {step.message}
              </div>
              {index < sequence.length - 1 && <div className="absolute left-4 top-8 bottom-0 border-l-2 border-dashed border-gray-200 -mb-4" data-id="element-775"></div>}
            </div>)}
        </div>
      </div>
      {sequence.length > 1 && !sequenceActive && <div className="bg-blue-50 p-3 rounded-md border border-blue-100 mb-4" data-id="element-776">
          <div className="flex items-start" data-id="element-777">
            <InfoIcon size={16} className="text-blue-500 mt-0.5 mr-2" data-id="element-778" />
            <div className="text-sm text-blue-700" data-id="element-779">
              <p className="font-medium mb-1" data-id="element-780">Sequence Summary</p>
              <p data-id="element-781">
                This {sequence.length}-step sequence will run over{' '}
                {totalDuration} days using{' '}
                {sequence.filter(s => s.channel === 'linkedin').length}{' '}
                LinkedIn messages,{' '}
                {sequence.filter(s => s.channel === 'email').length} emails,
                and {sequence.filter(s => s.channel === 'call').length} call
                reminders.
              </p>
            </div>
          </div>
        </div>}
      {!sequenceActive && <div className="flex justify-end mt-6" data-id="element-782">
          <button onClick={handleActivateSequence} className="px-4 py-2 bg-[#f9b30f] hover:bg-[#e0a40e] rounded-md text-sm font-medium text-white" disabled={sequence.length < 2} data-id="element-783">
            Activate Sequence
          </button>
        </div>}
    </div>;
};